import { get } from 'lodash'

import { components, generateUniqueKey, icons } from '@/utils'

import tag from '@/components/tag'
import textOverflow from '@/components/text/overflow'

function renderLabel(h) {
  if (this.hasLabel) {
    return h(
      'div',
      {
        class: {
          [`${components.select}__label`]: true,
          [this.focusedClass]: this.isFocused
        }
      },
      [ this.label, this.required && '*' ].filter(item => {
        return !!item
      }).join(' ')
    )
  }

  return h('div')
}

function renderGroup(h, nodes = []) {
  return h(
    'div',
    {
      class: {
        [`${components.select}__group`]: true,
        [`${components.select}__group--filled`]: !this.proxyIsEmpty && this.isFocused,
        [`${components.select}__group--one-row`]: this.hasInput && this.hideSelections && this.isFocused
      }
    },
    [ ...nodes ]
  )
}

function renderInput(h) {
  if (!this.isDisabled && this.hasInput) {
    return h(
      components.input,
      {
        class: {
          [`${components.select}__input`]: true,
          [`${components.select}__input--hidden`]: !this.isFocused
        },
        props: {
          value: this.search,
          placeholder: this.placeholder,
          disabled: this.disabled,
          readonly: this.readonly,
          tabindex: this.tabindex,
          mask: this.mask,
          maskOptions: this.maskOptions,
          processValue: this.processValue
        },
        on: {
          click: this._preventClick,
          keyup: this._onKeyup,
          keydown: this._onKeydown,
          input: this._inputHandler,
          paste: this._pasteHandler
        },
        ref: 'input'
      }
    )
  }
}

function renderClearable(h) {
  if (this.isClearable) {
    return h(
      components.icon,
      {
        class: { [this.focusedClass]: this.isFocused },
        props: { value: icons.clear },
        on: {
          click: event => {
            event.stopPropagation()
            this.proxy = []
          }
        }
      }
    )
  }
}

function renderLoading(h) {
  if (this.loading || this._data._loading) {
    return h(
      'div',
      { class: `${components.select}__loading` }
    )
  }
}

function renderSelectedChip(h, value, title, loading = false) {
  let as = 'select'
  if (this.dense) {
    as = 'select-dense'
  }

  return h(
    tag,
    {
      props: {
        as,
        label: title,
        loading,
        cancelable: this.multiple,
        cancel: () => {
          return this.removeByValue(value)
        }
      }
    }
  )
}
function renderSelectedItem(h, item) {
  const value = get(item, this.$_itemValue, item)
  const title = get(item, this.itemTitle, this._findTitleInSelectedItemsByValue(item))
  const loading = get(item, '_loading', false)

  return renderSelectedChip.call(this, h, value, title, loading)
}
function renderSelectionSlot(h, item, index) {
  if (this.$scopedSlots.selection) {
    return this.$scopedSlots.selection({
      item,
      index,
      addByValue: this.addByValue,
      removeByValue: this.removeByValue,
      toggleByValue: this.toggleByValue
    })
  } else {
    return renderSelectedItem.call(this, h, item, index)
  }
}
function renderSelection(h) {
  if (!this.proxyIsEmpty) {
    return h(
      'div',
      {
        class: {
          [`${components.select}__selection`]: true,
          [`${components.select}__selection--fill`]: !this.hasInput || !this.isFocused,
          [`${components.select}__selection--hidden`]: this.hasInput && this.hideSelections && this.isFocused
        }
      },
      [
        this.selectedItems.map((item, index) => {
          return renderSelectionSlot.call(this, h, item, index)
        })
      ]
    )
  }

  return h('div')
}

function renderBeforeSlot(h) {
  if (this.hasBeforeSlot) {
    let before = this.$slots.before
    if (this.$scopedSlots.before) {
      before = this.$scopedSlots.before()
    }

    return h(
      'div',
      {
        class: `${components.select}__before-holder`
      },
      [ before ]
    )
  }
}
function renderBeforeIcon(h) {
  if (this.beforeIcon) {
    return h(
      'div',
      {
        class: `${components.select}__before-holder`
      },
      [
        h(
          components.icon,
          {
            class: { [this.focusedClass]: this.isFocused },
            props: { value: this.beforeIcon },
            on: {
              click: () => {
                if (!this.isDisabled && typeof this.beforeIconCallback === 'function') {
                  this.beforeIconCallback()
                }
              }
            }
          }
        )
      ]
    )
  }
}
function renderBeforeContent(h) {
  return renderBeforeIcon.call(this, h)
}
function renderBefore(h) {
  if (this.hasBefore) {
    return h(
      'div',
      {
        class: {
          [`${components.select}__before`]: true,
          [`${components.select}__before--pointer`]: this.beforeIcon && typeof this.beforeIconCallback === 'function'
        }
      },
      [ renderBeforeSlot.call(this, h) || renderBeforeContent.call(this, h) ]
    )
  }
}

function renderAfterSlot(h) {
  if (this.hasAfterSlot) {
    let after = this.$slots.after
    if (this.$scopedSlots.after) {
      after = this.$scopedSlots.after()
    }

    return h(
      'div',
      {
        class: `${components.select}__after-holder`
      },
      [ after ]
    )
  }
}
function renderAfterIcon(h) {
  if (this.afterIcon) {
    return h(
      'div',
      {
        class: `${components.select}__after-holder`
      },
      [
        h(
          components.icon,
          {
            class: { [this.focusedClass]: this.isFocused },
            props: { value: this.afterIcon },
            on: {
              click: () => {
                if (!this.isDisabled && typeof this.afterIconCallback === 'function') {
                  this.afterIconCallback()
                }
              }
            }
          }
        )
      ]
    )
  }
}
function renderAfterContent(h) {
  return renderClearable.call(this, h) || renderAfterIcon.call(this, h)
}
function renderAfter(h) {
  if (this.hasAfter) {
    return h(
      'div',
      {
        class: {
          [`${components.select}__after`]: true,
          [`${components.select}__after--pointer`]: this.afterIcon && typeof this.afterIconCallback === 'function'
        }
      },
      [ renderAfterSlot.call(this, h) || renderAfterContent.call(this, h) ]
    )
  }
}

function renderHolder(h) {
  return h(
    'div',
    {
      class: `${components.select}__holder`,
      style: { 'background-color': this.backgroundColor }
    },
    [
      renderBefore.call(this, h),
      renderGroup.call(this, h, [ renderSelection.call(this, h), renderInput.call(this, h) ]),
      renderAfter.call(this, h),
      renderLoading.call(this, h)
    ]
  )
}

function renderItemSlot(h, item, index) {
  const title = get(item, this.itemTitle, this._findTitleInSelectedItemsByValue(item))

  if (typeof this.$scopedSlots.item === 'function') {
    return this.$scopedSlots.item({
      item,
      index
    })
  }

  return h(
    textOverflow,
    {
      props: {
        value: title,
        font: { size: 13 }
      }
    }
  )
}
function renderItem(h, item, index) {
  if (typeof this.$scopedSlots.listItem === 'function') {
    return this.$scopedSlots.listItem({
      item,
      index,
      addByValue: this.addByValue,
      removeByValue: this.removeByValue,
      toggleByValue: this.toggleByValue,
      selectedItems: this.selectedItems
    })
  }

  let suffix
  if (this.multiple) {
    suffix = icons.check_box_outline_blank
    if (item._selected) {
      suffix = icons.check_box
    }
  }

  return h(
    components['list-item'],
    {
      props: {
        active: item._selected && !this.multiple,
        hovered: item._hovered,
        disabled: item._disabled,
        icon: item._icon,
        iconSVG: item._iconSVG,
        iconColor: item._iconColor,
        suffix,
        color: item._color,
        dense: this.dense
      },
      on: {
        click: event => {
          this._itemClickHandler(event, item)
        }
      },
      key: generateUniqueKey()
    },
    [ renderItemSlot.call(this, h, item, index) ]
  )
}
function renderEmptyState(h) {
  return h(
    'div',
    {
      class: `${components.select}__empty`
    },
    [
      h(
        components.empty,
        {
          props: {
            padless: true,
            size: 56
          }
        }
      )
    ]
  )
}
function renderItems(h) {
  if (this.filteredItems.length === 0) {
    return renderEmptyState.call(this, h)
  }

  return h(
    components.list,
    {
      props: { dense: this.dense },
      key: generateUniqueKey()
    },
    [
      this.filteredItems.map((item, index) => {
        return renderItem.call(this, h, item, index)
      })
    ]
  )
}

function renderMenu(h) {
  if (!this.isDisabled) {
    return h(
      components.menu,
      {
        props: this.$_menuProps,
        on: {
          input: event => {
            this._data._showDropdown = event
          }
        },
        ref: 'menu'
      },
      [ renderItems.call(this, h) ]
    )
  }
}

function renderAttach(h) {
  return h(
    'div',
    {
      class: `${components.select}__attach`,
      ref: 'attach'
    }
  )
}

function renderDetails(h) {
  if (this.details) {
    let leftHtml = this.hint
    if (this.isError && this._data._error) {
      leftHtml = this._data._error
    }

    return h(
      'div',
      {
        class: `${components.select}__details`
      },
      [
        h(
          'div',
          {
            class: `${components.select}__details--left`,
            domProps: { innerHTML: leftHtml }
          }
        ),

        h(
          'div',
          {
            class: `${components.select}__details--right`
          },
          this.count
        )
      ]
    )
  }
}

export default function(h) {
  return h(
    'div',
    {
      class: {
        [`${components.select}`]: true,
        [`${components.select}--${this.labelMode}`]: true,
        [`${components.select}--labeled`]: !!this.label,
        [`${components.select}--disabled`]: this.isDisabled,
        [`${components.select}--autocomplete`]: !!this.autocomplete,
        [`${components.select}--filled`]: !this.proxyIsEmpty,
        [`${components.select}--active`]: this.isActive,
        [`${components.select}--loading`]: this.loading || this.isLoading,
        [`${components.select}--flat`]: this.flat,
        [`${components.select}--dense`]: this.dense,
        [`${components.select}--search`]: !!this.search,
        [`${components.select}--error`]: this.isError,
        [`${components.select}--multiple`]: this.multiple,
        [`${components.select}--has-before`]: this.hasBefore,
        [`${components.select}--has-after`]: this.hasAfter
      },
      attrs: { tabindex: this.$_tabindex },
      on: this.rootListeners
    },
    [
      renderLabel.call(this, h),
      renderHolder.call(this, h),
      renderAttach.call(this, h),
      renderMenu.call(this, h),
      renderDetails.call(this, h)
    ]
  )
}
